'use client'

import { Button, Cell, Container, Dialog, Divider, Navigation, Spacer, Text } from '@vinted/web-ui'

import { X24 } from '@vinted/monochrome-icons'

import { MouseEvent } from 'react'
import { useIntl } from 'react-intl'

import { CurrencyAmountModel, formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import {
  bundlesPolicyHelpCenterLinkClickEvent,
  BundlingLimitationsErrorType,
} from '@marketplace-web/bundles/items-bundle-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'

type Props = {
  isOpen: boolean
  limit: CurrencyAmountModel | null
  type: BundlingLimitationsErrorType | null
  bundleTrackingId: string | null
  screen: string
  onClose: () => void
}

const BundlingLimitationsModal = ({
  isOpen,
  limit,
  type,
  bundleTrackingId,
  screen,
  onClose,
}: Props) => {
  const { locale } = useIntl()
  const { track } = useTracking()
  const translate = useTranslate('bundle_items')

  const handleBodyClick = (event: MouseEvent) => {
    if (!(event.target instanceof HTMLAnchorElement)) return

    track(
      bundlesPolicyHelpCenterLinkClickEvent({
        screen,
        target: 'bundles_policy_help_centre_link',
        bundleId: bundleTrackingId ?? '',
      }),
    )
  }

  const formatBody = (body: string) => {
    if (!limit) return body

    const formatAmount = formatCurrencyAmount(
      {
        amount: limit.amount,
        currencyCode: limit.currencyCode,
      },
      locale,
    )

    return body.replace('%{price}', formatAmount)
  }

  const renderItemNotAddedModal = () => {
    return (
      <Dialog show={isOpen} defaultCallback={onClose} closeOnOverlay>
        <Navigation
          body={
            <Text
              as="h2"
              text={translate('item_not_added_modal.title')}
              alignment="center"
              type="title"
            />
          }
          right={
            <Button
              iconName={X24}
              styling="flat"
              theme="amplified"
              inline
              onClick={onClose}
              testId="cant-bundle-items-modal-close"
              aria={{
                'aria-label': translate('item_not_added_modal.close'),
              }}
            />
          }
        />
        <Divider />
        <Cell onClick={handleBodyClick}>
          <Text
            as="h3"
            html
            text={
              <div className="bundling-limitations-modal-body">
                {formatBody(translate('item_not_added_modal.body'))}
              </div>
            }
            type="subtitle"
          />
        </Cell>
        <Spacer size="regular" />
        <Container>
          <Button
            styling="filled"
            text={translate('item_not_added_modal.close')}
            onClick={onClose}
          />
        </Container>
      </Dialog>
    )
  }

  const renderBundleCostsTooMuchModal = () => {
    return (
      <Dialog show={isOpen} defaultCallback={onClose} closeOnOverlay>
        <Navigation
          body={
            <Text
              as="h2"
              text={translate('bundle_costs_too_much_modal.title')}
              alignment="center"
              type="title"
            />
          }
          right={
            <Button
              iconName={X24}
              styling="flat"
              theme="amplified"
              inline
              onClick={onClose}
              testId="cant-bundle-items-modal-close"
              aria={{
                'aria-label': translate('bundle_costs_too_much_modal.close'),
              }}
            />
          }
        />
        <Divider />
        <Cell onClick={handleBodyClick}>
          <Text
            as="h3"
            html
            text={
              <div className="bundling-limitations-modal-body">
                {formatBody(translate('bundle_costs_too_much_modal.body'))}
              </div>
            }
            type="subtitle"
          />
        </Cell>
        <Spacer size="regular" />
        <Container>
          <Button
            styling="filled"
            text={translate('bundle_costs_too_much_modal.close')}
            onClick={onClose}
          />
        </Container>
      </Dialog>
    )
  }

  const renderModal = () => {
    switch (type) {
      case BundlingLimitationsErrorType.ItemExceedsLimit:
        return renderItemNotAddedModal()
      case BundlingLimitationsErrorType.BundleExceedsLimit:
        return renderBundleCostsTooMuchModal()
      default:
        return null
    }
  }

  return renderModal()
}

export default BundlingLimitationsModal
